import { request } from "../../../services/api"
import { createFormDataNested } from "../../../utils/form-data/create-form-data-nested"
import type {
  ProjectTypeTemplate,
  ProjectTypeTemplatePhase
} from "../types/project-type.types.ts"
import type {
  ProjectTypeAddPhaseData,
  ProjectTypeCopyData,
  ProjectTypeCreateData,
  ProjectTypeListParams,
  ProjectTypeListResponse,
  ProjectTypePartialUpdateData,
  ProjectTypeUpdateData,
  TemplateApiResponse,
  TemplateEntityId
} from "./services.types.ts"

const PROJECT_TYPES_BASE_URL = "/templates-management/project-types"

//
// DELETE
// /templates-management/project-types/{id}/phases/{phase_id}/
// Remove phase from project type
// templates-management_project-types_remove_phase
const PATHS = {
  // Project Types
  getProjectTypes: `${PROJECT_TYPES_BASE_URL}/`,
  createProjectType: `${PROJECT_TYPES_BASE_URL}/`,
  autocompleteProjectTypes: `${PROJECT_TYPES_BASE_URL}/autocomplete/`,
  getProjectTypeDetails: (id: TemplateEntityId) =>
    `${PROJECT_TYPES_BASE_URL}/${id}/`,
  updateProjectType: (id: TemplateEntityId) =>
    `${PROJECT_TYPES_BASE_URL}/${id}/`,
  partialUpdateProjectType: (id: TemplateEntityId) =>
    `${PROJECT_TYPES_BASE_URL}/${id}/`,
  deleteProjectType: (id: TemplateEntityId) =>
    `${PROJECT_TYPES_BASE_URL}/${id}/`,
  copyProjectType: (id: TemplateEntityId) =>
    `${PROJECT_TYPES_BASE_URL}/${id}/copy/`,
  resetProjectTypeToDefault: (id: TemplateEntityId) =>
    `${PROJECT_TYPES_BASE_URL}/${id}/reset-to-default/`,
  // Phases Action
  addPhase: (id: TemplateEntityId) => `${PROJECT_TYPES_BASE_URL}/${id}/phases/`,
  removePhase: (id: TemplateEntityId, phaseId: TemplateEntityId) =>
    `${PROJECT_TYPES_BASE_URL}/${id}/phases/${phaseId}/`
}

// Project Types API
export const getProjectTypes = (params?: ProjectTypeListParams) =>
  request.api.get<ProjectTypeListResponse, TemplateApiResponse<unknown>>(
    PATHS.getProjectTypes,
    params
  )

export const createProjectType = (data: ProjectTypeCreateData) => {
  if (!data?.file_list?.length) {
    return request.api.post<
      TemplateApiResponse<ProjectTypeTemplate>,
      TemplateApiResponse<unknown>
    >(PATHS.createProjectType, data)
  }

  const headers = { "Content-Type": "multipart/form-data" }
  const formData = createFormDataNested(data, "file")

  return request.api.post<
    TemplateApiResponse<ProjectTypeTemplate>,
    TemplateApiResponse<unknown>
  >(PATHS.createProjectType, formData, { headers })
}

export const autocompleteProjectTypes = (params?: ProjectTypeListParams) =>
  request.api.get<ProjectTypeTemplate[], TemplateApiResponse<unknown>>(
    PATHS.autocompleteProjectTypes,
    params
  )

export const getProjectTypeDetails = (projectTypeId: TemplateEntityId) =>
  request.api.get<ProjectTypeTemplate, TemplateApiResponse<unknown>>(
    PATHS.getProjectTypeDetails(projectTypeId)
  )

export const updateProjectType = ({ id, ...data }: ProjectTypeUpdateData) => {
  if (!data?.file_list?.length) {
    return request.api.put<
      TemplateApiResponse<ProjectTypeTemplate>,
      TemplateApiResponse<unknown>
    >(PATHS.updateProjectType(id), data)
  }

  const headers = { "Content-Type": "multipart/form-data" }
  const formData = createFormDataNested(data, "file")

  return request.api.put<
    TemplateApiResponse<ProjectTypeTemplate>,
    TemplateApiResponse<unknown>
  >(PATHS.updateProjectType(id), formData, { headers })
}

export const partialUpdateProjectType = ({
  id,
  ...data
}: ProjectTypePartialUpdateData) => {
  return request.api.patch<
    TemplateApiResponse<ProjectTypeTemplate>,
    TemplateApiResponse<unknown>
  >(PATHS.partialUpdateProjectType(id), data)
}

export const deleteProjectType = (id: TemplateEntityId) =>
  request.api.delete<TemplateApiResponse<null>, TemplateApiResponse<unknown>>(
    PATHS.deleteProjectType(id)
  )

export const copyProjectType = (
  id: TemplateEntityId,
  data: ProjectTypeCopyData
) =>
  request.api.post<
    TemplateApiResponse<ProjectTypeTemplate>,
    TemplateApiResponse<unknown>
  >(PATHS.copyProjectType(id), data)

export const resetProjectTypeToDefault = (id: TemplateEntityId) =>
  request.api.post<
    TemplateApiResponse<ProjectTypeTemplate>,
    TemplateApiResponse<unknown>
  >(PATHS.resetProjectTypeToDefault(id))

export const addPhaseToProjectType = (
  id: TemplateEntityId,
  phase: ProjectTypeAddPhaseData
) =>
  request.api.post<
    TemplateApiResponse<ProjectTypeTemplatePhase>,
    TemplateApiResponse<unknown>
  >(PATHS.addPhase(id), phase)

export const removePhaseFromProjectType = (
  id: TemplateEntityId,
  phaseId: TemplateEntityId
) =>
  request.api.delete<TemplateApiResponse<null>, TemplateApiResponse<unknown>>(
    PATHS.removePhase(id, phaseId)
  )
